(() => {
  const STORAGE_PREFIX = "countdown-announcement-dismissed:";
  const HEADER_SELECTOR = ".header-wrapper";
  const HEADER_CLOSED_CLASS = "countdown-announcement-closed";
  const SECTION_SELECTOR = ".shopify-section";
  const SECTION_CLOSED_CLASS = "countdown-announcement-section--closed";
  const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  const hasStorage = () => {
    try {
      const testKey = "__countdown_announcement_test__";
      window.localStorage.setItem(testKey, "1");
      window.localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  };

  const storageEnabled = hasStorage();

  const getDismissKey = (root) => {
    const key = root.dataset.dismissKey;
    if (!key) return null;
    return `${STORAGE_PREFIX}${key}`;
  };

  const isDismissed = (root) => {
    if (!storageEnabled || isDesignMode) return false;
    const key = getDismissKey(root);
    if (!key) return false;
    return window.localStorage.getItem(key) === "1";
  };

  const persistDismissed = (root) => {
    if (!storageEnabled || isDesignMode) return;
    const key = getDismissKey(root);
    if (!key) return;
    window.localStorage.setItem(key, "1");
  };

  // tick() calls hide() or show() every second. classList.add/remove write the class
  // attribute even when nothing changes, and each write is a mutation that session-
  // recording scripts on the page serialise, so these only touch a class that differs.
  const setClass = (el, name, on) => {
    if (el && el.classList.contains(name) !== on) el.classList.toggle(name, on);
  };

  const hide = (root) => {
    setClass(root, "is-hidden", true);

    // The section wrapper carries a fixed height so the bar cannot shift the page
    // while the font loads. Hiding only the bar left that height behind as a white
    // band across the top, so the wrapper is collapsed alongside it. This runs for
    // every reason the bar goes away — dismissed, expired, outside the daily
    // window — because the leftover band looks the same in all of them.
    const section = root.closest(SECTION_SELECTOR);
    setClass(section, SECTION_CLOSED_CLASS, true);

    // The sticky header's own "top" offset is only reset by the CSS :has() rule once
    // .header-wrapper carries this class. Only the dismiss paths used to add it, so
    // a bar that closed itself automatically (expired, or outside the daily window)
    // left the header stuck 73px+ below the viewport top with a blank band beneath
    // it for the rest of the session. hide() runs for every close reason, so putting
    // the call here covers all of them.
    addHeaderClosedClass();
  };

  const addHeaderClosedClass = () => {
    setClass(document.querySelector(HEADER_SELECTOR), HEADER_CLOSED_CLASS, true);
  };

  const show = (root) => {
    if (root.dataset.dismissedByUser === "true") return;
    setClass(root, "is-hidden", false);

    const section = root.closest(SECTION_SELECTOR);
    setClass(section, SECTION_CLOSED_CLASS, false);

    // Mirrors the addHeaderClosedClass() call in hide(): only reached from the
    // daily-window tick when the bar isn't user-dismissed, so it's always correct to
    // undo it here — the bar has reoccupied its height, so the header's sticky
    // offset needs to account for it again instead of sticking at top:0 underneath it.
    setClass(document.querySelector(HEADER_SELECTOR), HEADER_CLOSED_CLASS, false);
  };

  const parseHour = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(24, Math.max(0, parsed));
  };

  const parseEnd = (raw) => {
    if (!raw) return null;
    const normalized = raw.replace(" ", "T");
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  };

  /* Europe/London without Intl: BST runs from 01:00 UTC on the last Sunday of March to 01:00 UTC
     on the last Sunday of October. Same wall-clock Date as
     new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/London" })), without making
     this script the one that initialises ICU's time-zone data (80-240ms on a throttled phone).
     The pre-paint check in sections/countdown-announcement-bar.liquid uses the same arithmetic. */
  const londonNow = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const lastSunday = (month) => {
      const d = new Date(Date.UTC(year, month + 1, 0, 1));
      d.setUTCDate(d.getUTCDate() - d.getUTCDay());
      return d.getTime();
    };
    const t = now.getTime();
    const wall = new Date(t + (t >= lastSunday(2) && t < lastSunday(9) ? 3600000 : 0));
    return new Date(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate(), wall.getUTCHours(), wall.getUTCMinutes(), wall.getUTCSeconds());
  };

  const getNowInTimeZone = (timeZone) => {
    if (!timeZone) return new Date();
    if (timeZone === "Europe/London") return londonNow();
    try {
      return new Date(new Date().toLocaleString("en-US", { timeZone }));
    } catch (e) {
      return new Date();
    }
  };

  const getDailyWindow = (now, startHour, endHour) => {
    const start = new Date(now);
    start.setHours(startHour, 0, 0, 0);

    let end = new Date(now);
    if (endHour === 24) {
      end.setDate(end.getDate() + 1);
      end.setHours(0, 0, 0, 0);
    } else {
      end.setHours(endHour, 0, 0, 0);
      if (endHour <= startHour) {
        end.setDate(end.getDate() + 1);
      }
    }

    if (now < start) {
      return { active: false, end, start };
    }

    if (now >= end) {
      const nextStart = new Date(start);
      nextStart.setDate(nextStart.getDate() + 1);
      const nextEnd = new Date(end);
      nextEnd.setDate(nextEnd.getDate() + 1);
      return { active: false, end: nextEnd, start: nextStart };
    }

    return { active: true, end, start };
  };

  const updateTimer = (root, end, dailySubline, timeZone) => {
    const now = getNowInTimeZone(timeZone);
    const totalDiff = Math.max(0, end.getTime() - now.getTime());
    let diff = totalDiff;

    const days = Math.floor(diff / 86400000);
    diff %= 86400000;
    const hours = Math.floor(diff / 3600000);
    diff %= 3600000;
    const mins = Math.floor(diff / 60000);
    diff %= 60000;
    const secs = Math.floor(diff / 1000);

    // Only a digit that changed is written, into the existing text node: once a second
    // that is the seconds alone, where textContent used to replace all four nodes.
    const setText = (el, text) => {
      if (!el) return;
      const node = el.firstChild;
      if (node && node.nodeType === 3 && !node.nextSibling) {
        if (node.nodeValue !== text) node.nodeValue = text;
      } else if (el.textContent !== text) {
        el.textContent = text;
      }
    };
    const set = (part, value) => {
      setText(root.querySelector(`[data-part="${part}"]`), String(value).padStart(2, "0"));
    };

    set("days", days);
    set("hours", hours);
    set("mins", mins);
    set("secs", secs);

    if (dailySubline) {
      const hoursRemaining = Math.floor(totalDiff / 3600000);
      const subline = root.querySelector(".countdown-announcement__subline");
      setText(subline, `${hoursRemaining} hour${hoursRemaining === 1 ? "" : "s"} remaining`);
    }
  };

  const init = (root) => {
    const dailyMode = root.dataset.dailyMode !== "false";
    const dailyStartHour = parseHour(root.dataset.dailyStartHour, 1);
    const dailyEndHour = parseHour(root.dataset.dailyEndHour, 24);
    const dailySubline = root.dataset.dailySubline !== "false";
    const timeZone = root.dataset.timezone || "Europe/London";

    let end = null;
    if (isDismissed(root)) {
      root.dataset.dismissedByUser = "true";
      hide(root);
      addHeaderClosedClass();
      return;
    }

    if (!dailyMode) {
      end = parseEnd(root.dataset.endDatetime);
      if (!end || end.getTime() <= Date.now()) {
        hide(root);
        return;
      }
    }

    let interval = null;
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const tick = () => {
      if (root.dataset.dismissedByUser === "true") {
        hide(root);
        return;
      }

      if (dailyMode) {
        const now = getNowInTimeZone(timeZone);
        const window = getDailyWindow(now, dailyStartHour, dailyEndHour);
        if (!window.active) {
          hide(root);
          end = window.end;
          return;
        }
        show(root);
        end = window.end;
      }

      if (end) {
        updateTimer(root, end, dailySubline, timeZone);
      }
    };

    tick();
    interval = setInterval(() => {
      tick();
    }, 1000);

    /* end is a fixed timestamp and tick() always recomputes against the current
       time, so pausing while the tab is hidden and resuming later costs nothing
       in accuracy — just skips ticks nobody could see. */
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        stop();
      } else if (!interval) {
        tick();
        interval = setInterval(() => {
          tick();
        }, 1000);
      }
    });

    const closeBtn = root.querySelector(".countdown-announcement__close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        root.dataset.dismissedByUser = "true";
        hide(root);
        addHeaderClosedClass();
        persistDismissed(root);
        stop();
      });
    }
  };

  const mount = (container = document) => {
    const nodes = container.querySelectorAll(".countdown-announcement");
    nodes.forEach((node) => {
      if (node.dataset.countdownInit === "true") return;
      node.dataset.countdownInit = "true";
      init(node);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount());
  } else {
    mount();
  }

  document.addEventListener("shopify:section:load", (event) => {
    mount(event.target);
  });
})();
